"use client";

/**
 * The dashboard's dropdown: a custom, searchable replacement for <select>.
 *
 * Drop-in: same <option> / <optgroup> children (including lists made with .map and
 * fragments), value, disabled, id, className and aria-label; onChange gets an event-like
 * object whose target.value is the chosen value, so `onChange={(e) => set(e.target.value)}`
 * keeps working.
 *
 * Placement (works the same in tables, cards, modals and scrolling lists):
 * - drawn in a layer above everything (portal), so no card or table ever clips it;
 * - opens below the field, or above it when there's more room there, and its height is
 *   capped to the space actually available — it never runs off the top or bottom;
 * - as wide as the field (at least 240 px), shifted left when it would leave the screen;
 * - follows the field while the page or a table scrolls, and closes if the field scrolls away;
 * - on phones it opens as a sheet from the bottom, with large rows and a Done button.
 *
 * Search box, keyboard control (↑ ↓ Home End Enter Esc, typing filters), chosen option
 * ticked, matches highlighted, group headings, and a clear "No matches" with a reset.
 */
import { Children, Fragment, isValidElement, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { lockScroll } from "@/lib/scrollLock";

// local copy: ui.tsx imports this file, so it can't import from ui.tsx
function cx(...parts: (string | false | null | undefined)[]) {
  return parts.filter(Boolean).join(" ");
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

type ChangeEvent = { target: { value: string; name?: string }; currentTarget: { value: string; name?: string } };

function textOf(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children);
  return "";
}

/** <option> / <optgroup> children (nested in arrays and fragments) → options. */
export function optionsFrom(children: ReactNode): SelectOption[] {
  const out: SelectOption[] = [];
  const walk = (nodes: ReactNode, group?: string) => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      const props = child.props as { value?: string | number; children?: ReactNode; disabled?: boolean; label?: string };
      if (child.type === Fragment) {
        walk(props.children, group);
        return;
      }
      if (child.type === "option") {
        const label = textOf(props.children);
        out.push({ value: props.value !== undefined ? String(props.value) : label, label, disabled: props.disabled, group });
      } else if (child.type === "optgroup") {
        walk(props.children, props.label);
      }
    });
  };
  walk(children);
  return out;
}

const norm = (s: string) => s.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();
const ROW = 38; // px per option row, for sizing the list
const HEAD = 50; // search box
const GAP = 6; // between field and list
const EDGE = 8; // never closer than this to the window's edge

/** The field has scrolled out of a scrolling box it sits in (a table, a list, a card). */
function hiddenByScroller(el: HTMLElement, r: DOMRect): boolean {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const s = getComputedStyle(p);
    if (!/(auto|scroll|hidden)/.test(s.overflowY + s.overflowX)) continue;
    const b = p.getBoundingClientRect();
    if (r.bottom < b.top + 4 || r.top > b.bottom - 4 || r.right < b.left + 4 || r.left > b.right - 4) return true;
  }
  return false;
}

type Pos = { left: number; top?: number; bottom?: number; width: number; maxH: number; up: boolean };

function useIsPhone() {
  const [phone, setPhone] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(max-width: 639px)");
    const on = () => setPhone(m.matches);
    on();
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return phone;
}

export function Select({
  value,
  onChange,
  children,
  options: given,
  id,
  name,
  className,
  disabled,
  placeholder = "Choose…",
  searchPlaceholder = "Search…",
  "aria-label": ariaLabel,
  title,
}: {
  value?: string | number | null;
  onChange?: (e: ChangeEvent) => void;
  children?: ReactNode;
  options?: SelectOption[];
  id?: string;
  name?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  "aria-label"?: string;
  title?: string;
}) {
  const options = useMemo(() => given ?? optionsFrom(children), [given, children]);
  const current = value == null ? "" : String(value);
  const selected = options.find((o) => o.value === current);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<Pos | null>(null);
  const phone = useIsPhone();
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const listId = useId();

  const shown = useMemo(() => {
    const n = norm(q.trim());
    return n ? options.filter((o) => norm(o.label).includes(n) || norm(o.value).includes(n) || (o.group && norm(o.group).includes(n))) : options;
  }, [options, q]);
  const groups = useMemo(() => new Set(shown.map((o) => o.group).filter(Boolean)).size, [shown]);

  const place = () => {
    const el = button.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vv = window.visualViewport;
    const vh = vv?.height ?? window.innerHeight;
    const vw = vv?.width ?? window.innerWidth;
    // the field scrolled out of view (a table or the page moved): close instead of floating somewhere odd
    if (r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw || hiddenByScroller(el, r)) {
      setOpen(false);
      return;
    }
    const want = Math.min(380, HEAD + Math.max(1, shown.length + groups) * ROW + 10);
    const below = vh - r.bottom - GAP - EDGE;
    const above = r.top - GAP - EDGE;
    const up = below < want && above > below;
    const room = Math.max(120, up ? above : below);
    const width = Math.min(Math.max(r.width, 240), vw - EDGE * 2, 440);
    let left = r.left;
    if (left + width > vw - EDGE) left = Math.max(EDGE, r.right - width);
    left = Math.max(EDGE, Math.min(left, vw - width - EDGE));
    setPos({ left, width, up, maxH: Math.min(want, room), ...(up ? { bottom: vh - r.top + GAP } : { top: r.bottom + GAP }) });
  };

  useLayoutEffect(() => {
    if (!open || phone) return;
    place();
    const onMove = () => place();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true); // any scrolling parent: tables, cards, the page
    window.visualViewport?.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
      window.visualViewport?.removeEventListener("resize", onMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-place when the list length changes too
  }, [open, phone, shown.length]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (!panel.current?.contains(t) && !button.current?.contains(t)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- start on the chosen option
    setActive(Math.max(0, shown.findIndex((o) => o.value === current)));
    // (desktop: the search box gets focus as soon as it is drawn, see below; on phones the keyboard would cover the list)
    const unlock = phone ? lockScroll() : null;
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
      unlock?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when opening
  }, [open]);

  useLayoutEffect(() => {
    if (open && !phone && pos) search.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once it is drawn
  }, [open, pos === null]);

  useEffect(() => {
    if (open) list.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const choose = (o: SelectOption) => {
    if (o.disabled) return;
    setOpen(false);
    setQ("");
    button.current?.focus();
    if (o.value !== current) onChange?.({ target: { value: o.value, name }, currentTarget: { value: o.value, name } });
  };

  const step = (from: number, dir: 1 | -1) => {
    for (let i = from + dir; i >= 0 && i < shown.length; i += dir) if (!shown[i].disabled) return i;
    return from;
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => step(a, 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => step(a, -1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(step(-1, 1));
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(step(shown.length, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (shown[active]) choose(shown[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      button.current?.focus();
    } else if (e.key === "Tab") {
      // back on the field, so the next Tab moves on from there (inside a dialog too)
      e.preventDefault();
      setOpen(false);
      button.current?.focus();
    }
  };

  const mark = (label: string) => {
    const n = norm(q.trim());
    if (!n) return label;
    const i = norm(label).indexOf(n);
    if (i < 0) return label;
    return (
      <>
        {label.slice(0, i)}
        <mark className="rounded-sm bg-[#FFE58A] px-0 text-rb-ink">{label.slice(i, i + n.length)}</mark>
        {label.slice(i + n.length)}
      </>
    );
  };

  const rows: ReactNode[] = [];
  let lastGroup: string | undefined;
  shown.forEach((o, i) => {
    if (o.group && o.group !== lastGroup) {
      rows.push(<li key={`g-${o.group}-${i}`} role="presentation" className="sticky top-0 z-[1] bg-white/95 px-3 pb-1 pt-2.5 text-[10.5px] font-bold uppercase tracking-wider text-rb-faint backdrop-blur">{o.group}</li>);
    }
    lastGroup = o.group;
    const on = o.value === current;
    rows.push(
      <li
        key={o.value + i}
        data-i={i}
        role="option"
        aria-selected={on}
        aria-disabled={o.disabled || undefined}
        onMouseEnter={() => setActive(i)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => choose(o)}
        className={cx(
          "mx-1.5 flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-left",
          phone ? "min-h-[46px] text-[15px]" : "min-h-[36px] text-[13.5px]",
          i === active && !on && "bg-rb-surface-2",
          on && "bg-rb-brand-soft font-semibold text-rb-brand-deep",
          o.disabled && "cursor-not-allowed opacity-45",
        )}
      >
        <span className={cx("flex h-4 w-4 flex-shrink-0 items-center justify-center", !on && "opacity-0")}><Check size={15} strokeWidth={2.6} /></span>
        <span className="min-w-0 flex-1 py-1.5 leading-snug [overflow-wrap:anywhere]">{mark(o.label)}</span>
      </li>,
    );
  });

  const searchBox = (
    <div className="flex items-center gap-2 border-b border-rb-line px-3 py-2">
      <Search size={15} className="flex-shrink-0 text-rb-faint" />
      <input
        ref={search}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(0);
        }}
        placeholder={searchPlaceholder}
        aria-label="Search the list"
        aria-controls={listId}
        className={cx("min-w-0 flex-1 bg-transparent outline-none placeholder:text-rb-faint", phone ? "h-9 text-[16px]" : "h-8 text-[13.5px]")}
      />
      {q && (
        <button type="button" onClick={() => { setQ(""); search.current?.focus(); }} className="rounded-full p-1 text-rb-faint hover:bg-rb-surface-2 hover:text-rb-ink" aria-label="Clear the search">
          <X size={13} />
        </button>
      )}
      <span className="rb-num flex-shrink-0 text-[11px] text-rb-faint">{shown.length}</span>
    </div>
  );

  const body = (
    <ul ref={list} id={listId} role="listbox" aria-label={ariaLabel} className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1.5">
      {shown.length === 0 ? (
        <li className="px-4 py-5 text-center text-[13px] text-rb-muted">
          No matches for “{q}”.{" "}
          <button type="button" onClick={() => setQ("")} className="font-semibold text-rb-brand-deep hover:underline">Show all</button>
        </li>
      ) : rows}
    </ul>
  );

  const base = className || "rb-input";
  return (
    <>
      <button
        ref={button}
        id={id}
        type="button"
        title={title ?? selected?.label}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen(true);
          } else if (e.key.length === 1 && /\S/.test(e.key) && !e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            setQ((cur) => (open ? cur + e.key : e.key)); // typing on the field searches, even before the box has focus
            setActive(0);
            setOpen(true);
            search.current?.focus();
          }
        }}
        className={cx(base, "!flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60", open && "ring-2 ring-rb-brand/25")}
      >
        <span className={cx("min-w-0 flex-1 truncate", !selected && "text-rb-faint")}>{selected?.label ?? placeholder}</span>
        <ChevronDown size={15} className={cx("flex-shrink-0 text-rb-muted transition-transform duration-150", open && "rotate-180")} />
      </button>
      {open && typeof document !== "undefined" &&
        (phone
          ? createPortal(
              <div className="fixed inset-0 z-[1150] flex flex-col justify-end bg-black/40" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
                <div ref={panel} onKeyDown={onKey} role="dialog" aria-label={ariaLabel || placeholder}
                  className="flex max-h-[75vh] flex-col overflow-hidden rounded-t-3xl bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl">
                  <div className="flex items-center justify-between px-4 pb-1 pt-3">
                    <span className="mx-auto h-1 w-10 rounded-full bg-rb-line" aria-hidden />
                  </div>
                  <div className="flex items-center justify-between px-4 pb-2">
                    <span className="truncate text-[15px] font-bold">{ariaLabel || placeholder}</span>
                    <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-3 py-1.5 text-[14px] font-semibold text-rb-brand-deep hover:bg-rb-brand-soft">Done</button>
                  </div>
                  {searchBox}
                  {body}
                </div>
              </div>,
              document.body,
            )
          : pos &&
            createPortal(
              <div
                ref={panel}
                onKeyDown={onKey}
                className={cx("fixed z-[1150] flex flex-col overflow-hidden rounded-2xl border border-rb-line bg-white shadow-[0_18px_50px_-12px_rgba(20,15,51,0.35)] ring-1 ring-black/5", pos.up ? "origin-bottom" : "origin-top")}
                style={{ left: pos.left, width: pos.width, maxHeight: pos.maxH, top: pos.top, bottom: pos.bottom }}
              >
                {searchBox}
                {body}
              </div>,
              document.body,
            ))}
    </>
  );
}
